"use client";

import {
  getBikeClass,
  type BikeClassKeyOrUnknown,
} from "@/lib/mobility/bike-classes";
import {
  bikeTypeLabel,
  rideStyleLabel,
  type BikeType,
  type RideStyle,
  type Transmission,
} from "@/lib/mobility/bike-facets";
import { cn } from "@/lib/utils";
import {
  CurrencyCodeAmount,
  currencyAmountLength,
} from "@/components/CurrencyCodeAmount";
import { pickLabel, useHub, useHubDensity } from "./hub-context";
import { HUB_GUTTER, MetaBadge, Thumb } from "./hub-ui";

/**
 * 차종·모델 행 — 지점의 차종 목록과 카탈로그가 같은 행을 쓴다.
 * 사진(136×102 · 데스크톱 152×114) / 모델명(두 줄까지, 제조사는 제목에 포함) /
 * 배지(클래스 · 타입 · AT/MT) · (카탈로그) 지점 수 — 같은 줄 오른쪽에 1일가 "/일" /
 * 라이딩 스타일 한 줄(있을 때만).
 *
 * 요금제 표기("인기 지점")와 기간 총액(2·7·30일)은 행에 싣지 않는다 —
 * 고르는 단계에서는 차종·클래스·1일가만 보면 된다(기간가는 상품 상세에서).
 */
export function ItemRow({
  image,
  name,
  classKey,
  bikeType,
  transmission,
  rideStyles,
  price,
  periodPrices,
  extraMeta,
}: {
  image?: string | null;
  /** 요금제 꼬리표를 뗀 모델명 — 상품명 그대로 */
  name: string;
  classKey?: BikeClassKeyOrUnknown | string;
  /** 제조사 표시명(이미 언어 변환된 값) — 없으면 줄을 만들지 않는다 */
  bikeType?: BikeType | null;
  transmission?: Transmission | null;
  rideStyles?: readonly RideStyle[];
  /** 1일가(표시 문자열) */
  /** 1일가 한 줄 (카탈로그 등 기존 호출부). periodPrices 가 있으면 무시된다. */
  price?: string | null;
  /** 기간별 총액 3칸(1·3·7일) — 지점의 차종 목록이 쓴다. */
  periodPrices?: Array<{ label: string; value: string | null }>;
  /** 카탈로그의 "지점 N곳" — 배지 줄 끝에 붙는다 */
  extraMeta?: string | null;
}) {
  const { t, locale } = useHub();
  const wide = useHubDensity() === "wide";

  const bikeClass = classKey
    ? getBikeClass(classKey as BikeClassKeyOrUnknown)
    : undefined;
  const badges = [
    bikeClass ? pickLabel(bikeClass.label, locale) : null,
    bikeType ? bikeTypeLabel(bikeType, locale) : null,
    transmission ?? null,
  ].filter((label): label is string => !!label);
  const rideLine = (rideStyles ?? [])
    .map((key) => rideStyleLabel(key, locale))
    .join(" · ");

  return (
    <div className={cn("w-full py-3", HUB_GUTTER)}>
      <div className="flex items-start gap-3">
        <Thumb
          src={image}
          alt={name}
          // 데스크톱은 2열이라 사진을 조금 줄인다(09-23) — 한 열이던 시절의 152×114 는
          // 좁아진 칸에서 글자 자리를 다 먹는다.
          w={wide ? 118 : 136}
          h={wide ? 88 : 102}
          // 넓은 데스크톱(1280+)은 한 단계 크게. 1280부터 목록이 반반으로 좁아지므로
          // 136×102 가 상한 — 더 키우면 칸 폭 ~270px 에서 이름 자리가 100px 아래로 준다(09-23).
          xl={wide ? { w: 136, h: 102 } : undefined}
          rounded="rounded-xl"
          bordered
        />
        <div className="min-w-0 flex-1">
          {/* 이름은 자르지 않는다 — 두 줄까지, 가격과 같은 줄에 두지 않는다 */}
          <span className="block min-w-0 line-clamp-2 text-[15px] font-semibold text-gray-900 mapShow:text-base">
            {name}
          </span>

          {/* 배지·지점 수는 왼쪽, 1일가는 같은 줄 오른쪽(기간 표가 있으면 오른쪽 비움) */}
          {badges.length > 0 || extraMeta || (price && !periodPrices) ? (
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {badges.map((label) => (
                  <MetaBadge key={label} label={label} />
                ))}
                {extraMeta ? (
                  <span className="text-[12px] text-gray-500">{extraMeta}</span>
                ) : null}
              </div>
              {price && !periodPrices ? (
                <span
                  className="shrink-0 whitespace-nowrap"
                  suppressHydrationWarning
                >
                  <span className="text-[15px] font-bold text-gray-900">
                    <CurrencyCodeAmount value={price} />
                  </span>
                  <span className="ml-0.5 text-[12px] font-medium text-gray-500">
                    {t("hub.perDay")}
                  </span>
                </span>
              ) : null}
            </div>
          ) : null}

          {rideLine ? (
            <p className="mt-1 line-clamp-1 text-[12.5px] text-gray-700 mapShow:text-[13px]">
              {rideLine}
            </p>
          ) : null}
        </div>
      </div>

      {/* 기간별 총액 — 사진 아래 전체 폭 3칸(기간 회색 위, 총액 굵게 아래). 결제와 같은 티어 규칙.
          사진 옆 좁은 열에 두면 금액이 두 줄로 깨진다(09-22).
          카드 테두리를 없앤 뒤(09-29)로는 모바일·데스크톱 모두 회색 면 + 칸 사이 짧은 세로선 —
          면은 gray-100 으로 한 톤 진하게 해 흰 바탕에서 한 덩어리로 읽히게 한다. */}
      {periodPrices ? (
        // 칸 폭은 그리드 폭(컨테이너 쿼리 /price)으로 정한다 — JS 로 재지 않는다(10-15).
        // 그리드 폭 W → 칸 글자 자리 = W/3 − 좌우 여백 − 구분선 1px:
        //   데스크톱 2열 카드(목록 열 px-6 · gap-x-5 · 카드 *:px-3): 960 ≈ 220 → 56px ·
        //   1040 ≈ 244 → 64px · 1280 ≈ 262 → 66px · 1440 ≈ 302 → 80px.
        //   모바일 390(드로어 px-3) ≈ 366 → 101px · 태블릿(p-6 한 열) ≥ 592 → 176px+.
        // W < 330 이면 통화 코드를 숫자 위 한 줄로 올리고, W < 250 이면 여백·숫자를 한 단계 줄인다.
        // 일곱 자리 이상(1,234,567)은 숫자만 한 단계 더 줄인다.
        <div
          className="@container/price mt-2.5 grid grid-cols-3 divide-x divide-gray-300 rounded-[10px] bg-gray-100 py-2"
          suppressHydrationWarning
        >
          {periodPrices.map((cell) => (
            <div key={cell.label} className="min-w-0 px-2 @min-[250px]/price:px-2.5">
              <div className="text-left text-[11px] text-gray-500 mapShow:text-xs">{cell.label}</div>
              <div className="whitespace-nowrap text-right text-[13px] font-semibold tabular-nums text-gray-900 @min-[250px]/price:text-[14px] @min-[400px]/price:text-[15px]">
                {cell.value ? (
                  <CurrencyCodeAmount
                    value={cell.value}
                    codeClassName="block leading-none mt-0.5 @min-[330px]/price:inline @min-[330px]/price:mt-0"
                    amountClassName={
                      currencyAmountLength(cell.value) > 7
                        ? "text-[11px] @min-[250px]/price:text-[12px] @min-[330px]/price:text-[13px]"
                        : undefined
                    }
                  />
                ) : (
                  "—"
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
